import { useParams, generatePath, Outlet } from 'react-router-dom';
import {
  ButtonPseudo,
  ButtonPrimary,
  SidePanel,
  SidePanelType,
  Popup,
  PermissionEnum,
  SidePanelLoader,
} from '@ui/src';
import { useAppSelector } from '@store';
import FeeForm from '../../../components/common/fee/fee';
import { CustomerStatus, OrganizationType } from '../types';
import useFeeForm from './hooks/use-fee-form';

import ROUTES from '../../../routes';

const FeeFormHOC = ({ type }: { type: OrganizationType }) => {
  const { baseCurrency } = useAppSelector(
    (state) => state.customersDictionaries,
  );
  const { fee } = useAppSelector((state) => state.customerFeeForm);
  const { baseInformation } = useAppSelector(
    (state) => state.individualCustomers.info.general,
  );
  const { baseInformation: corporateBaseInformation } = useAppSelector(
    (state) => state.corporateCustomers.info.general,
  );
  const {
    currenciesList: currencies,
    activePermissions,
    currencies: currenciesList,
  } = useAppSelector((state) => state.root);

  let info = baseInformation;

  if (type === OrganizationType.CORPORATE) {
    info = corporateBaseInformation;
  }

  const { customerId, feeId } = useParams();

  const {
    isLoading,
    feeTypes,
    control,
    errors,
    setPanelIsOpen,
    logs,
    defaultFormValues,
    fields,
    isModified,
    onRestoreDefault,
    regularFee,
    feeCurrencyId,
    feeCascadeCurrenciesIds,
    setValue,
    panelIsOpen,
    isDirty,
    setPopupVisible,
    handleClose,
    requestInProgress,
    deleteInProgress,
    isRestoredToDefault,
    deleteFeeAction,
    onSubmit,
    reset,
    popupVisible,
  } = useFeeForm({
    customerId,
    feeId,
    type,
    fee,
    info,
  });

  let buttonText;
  let content;
  let transactionLink;

  if (feeId) {
    buttonText = 'Save';

    if (type === OrganizationType.INDIVIDUAL) {
      transactionLink = generatePath(
        ROUTES.CUSTOMERS_INDIVIDUAL_TARIFFS_CREATE_TRANSACTION,
        { customerId: customerId as string, feeId },
      );
    } else {
      transactionLink = generatePath(
        ROUTES.CUSTOMERS_CORPORATE_TARIFFS_CREATE_TRANSACTION,
        { customerId: customerId as string, feeId },
      );
    }
  } else {
    buttonText = 'Add Fee';
  }

  const organizationStatus =
    type === OrganizationType.INDIVIDUAL
      ? baseInformation?.status
      : corporateBaseInformation?.status;

  if (isLoading || !fee) {
    content = <SidePanelLoader />;
  } else {
    content = (
      <FeeForm
        feeTypes={feeTypes}
        control={control}
        errors={errors}
        onClose={() => setPanelIsOpen(false)}
        feeId={feeId}
        link={transactionLink}
        logs={logs}
        defaultFormValues={defaultFormValues}
        values={fields}
        isModified={isModified}
        onRestoreDefault={onRestoreDefault}
        baseCurrency={baseCurrency}
        currencies={currencies}
        isEditable={activePermissions?.includes(
          type === OrganizationType.INDIVIDUAL
            ? PermissionEnum.INDIVIDUAL_CUSTOMERS_EDIT_TARIFF_ACTION
            : PermissionEnum.CORPORATE_CUSTOMERS_EDIT_TARIFF_ACTION,
        )}
        regularFee={regularFee}
        feeCurrencyId={feeCurrencyId}
        feeCascadeCurrenciesIds={feeCascadeCurrenciesIds}
        setValue={setValue}
        status={organizationStatus}
        currenciesList={currenciesList}
      />
    );
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.Accounts}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={() => {
          if (isDirty) {
            setPopupVisible(true);
          } else {
            handleClose();
          }
        }}
        sidepanelExpandedKey="customers-fee"
        title={feeId ? 'Edit fee' : 'Add fee to customer tariff'}
        footer={
          <>
            {activePermissions?.includes(
              type === OrganizationType.INDIVIDUAL
                ? PermissionEnum.INDIVIDUAL_CUSTOMERS_EDIT_TARIFF_ACTION
                : PermissionEnum.CORPORATE_CUSTOMERS_EDIT_TARIFF_ACTION,
            ) &&
              organizationStatus !== CustomerStatus.CLOSED && (
                <ButtonPrimary
                  text={
                    requestInProgress || deleteInProgress
                      ? 'Loading...'
                      : buttonText
                  }
                  disabled={
                    Boolean(!fields?.name && !fields.feeType) ||
                    isLoading ||
                    deleteInProgress
                  }
                  onClick={() =>
                    isRestoredToDefault ? deleteFeeAction() : onSubmit()
                  }
                  bold
                />
              )}
            <ButtonPseudo
              text="Close"
              onClick={() => {
                if (isDirty) {
                  setPopupVisible(true);
                } else {
                  handleClose();
                }
              }}
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="Changes have not been saved"
        description="Close without saving?"
        onCancelClick={() => setPopupVisible(false)}
        onOkClick={() => {
          reset(defaultFormValues);
          setPopupVisible(false);
          handleClose();
        }}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={popupVisible}
        parent={document.getElementById('save-alert-popup') as HTMLElement}
      />
      <div id="save-alert-popup" />
      <Outlet />
    </>
  );
};

export default FeeFormHOC;
